import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, ArrowRight, AlertTriangle } from "lucide-react";

/** Edita un desglose ya aplicado: nuevo factor (×N → ×M) y/o costo por unidad. Factor 1 = deshacer. */
export default function EditarDesgloseDialog({ open, onClose, producto, factorActual, onDone }) {
  const [factor, setFactor] = useState("");
  const [costo, setCosto] = useState("");
  const [precios, setPrecios] = useState("no");
  const [pctMin, setPctMin] = useState("");
  const [pctSug, setPctSug] = useState("");
  const [precioMin, setPrecioMin] = useState("");
  const [precioSug, setPrecioSug] = useState("");
  const [motivo, setMotivo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFactor(String(factorActual || "")); setCosto(""); setPrecios("no"); setPctMin(""); setPctSug(""); setPrecioMin(""); setPrecioSug(""); setMotivo(""); setPreview(null);
  }, [open, factorActual]);

  const body = () => ({
    nuevo_factor: Number(factor), nuevo_costo_unitario: costo === "" ? null : Math.round(Number(costo)), ajustar_precios: precios,
    pct_margen_minimo: precios === "margenes" ? Number(pctMin) : null, pct_margen_sugerido: precios === "margenes" ? Number(pctSug) : null,
    precio_minimo_manual: precios === "manual" ? Math.round(Number(precioMin)) : null,
    precio_sugerido_manual: precios === "manual" ? Math.round(Number(precioSug)) : null,
    motivo: motivo.trim() || null,
  });

  const calcular = async () => {
    if (!Number(factor) || Number(factor) < 1) return toast.error("El factor debe ser 1 (deshacer) o más");
    if (precios === "manual" && (precioMin === "" || precioSug === "")) return toast.error("Escribe el precio mínimo y el sugerido");
    setLoading(true);
    try { const { data } = await api.post(`/productos/${producto.id}/desglosar/editar/preview`, body()); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular"); }
    finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (motivo.trim().length < 5) return toast.error("Escribe el motivo (mínimo 5 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/${producto.id}/desglosar/editar`, body());
      toast.success(`Desglose corregido ×${data.factor_anterior} → ×${data.factor_nuevo} · stock ${formatNumber(data.stock_viejo)} → ${formatNumber(data.stock_nuevo)}`);
      onDone?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    finally { setSaving(false); }
  };

  const f = preview?.filas?.[0];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="editar-desglose-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Pencil className="w-5 h-5 text-primary" /> Editar desglose · {producto?.nombre}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            El desglose actual es <b>×{factorActual}</b>. Puedes cambiar cuántas unidades trae el paquete y/o fijar el costo por unidad. Todo se recalcula desde los valores originales del contenedor, respetando lo que ya vendiste, y el stock de la ficha se sincroniza con los lotes. Factor <b>1</b> = deshacer el desglose.
          </p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label>Unidades por paquete (nuevo ×N)</Label>
            <Input type="number" min="1" value={factor} onChange={(e) => { setFactor(e.target.value); setPreview(null); }} className="h-11 mt-1" data-testid="editar-desglose-factor" />
          </div>
          <div>
            <Label>Costo por unidad (COP) · opcional</Label>
            <Input type="number" min="0" value={costo} onChange={(e) => { setCosto(e.target.value); setPreview(null); }} className="h-11 mt-1" placeholder={f ? `Automático: ${formatCOP(Math.round(f.costo_paquete / (Number(factor) || 1)))}` : "Vacío = costo del paquete ÷ N"} data-testid="editar-desglose-costo" />
          </div>
          <div className="col-span-2">
            <Label>Precios de venta</Label>
            <select value={precios} onChange={(e) => { setPrecios(e.target.value); setPreview(null); }} className="h-11 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="editar-desglose-precios">
              <option value="no">No tocar precios</option>
              <option value="restaurar">Restaurar los precios de antes del desglose (si se dividieron por error)</option>
              <option value="dividir">Re-escalar por el cambio de factor (×N/M)</option>
              <option value="margenes">Recalcular con % de margen sobre el costo nuevo</option>
              <option value="manual">Escribir yo los precios (mín / sugerido)</option>
            </select>
          </div>
          {precios === "manual" && (
            <>
              <div><Label className="text-xs">Precio mínimo (COP por unidad)</Label><Input type="number" min="0" value={precioMin} onChange={(e) => { setPrecioMin(e.target.value); setPreview(null); }} className="h-10 mt-1" placeholder={producto?.precio_minimo ? `Actual: ${formatCOP(producto.precio_minimo)}` : ""} data-testid="editar-desglose-precio-min" /></div>
              <div><Label className="text-xs">Precio sugerido (COP por unidad)</Label><Input type="number" min="0" value={precioSug} onChange={(e) => { setPrecioSug(e.target.value); setPreview(null); }} className="h-10 mt-1" placeholder={producto?.precio_sugerido ? `Actual: ${formatCOP(producto.precio_sugerido)}` : ""} data-testid="editar-desglose-precio-sug" /></div>
            </>
          )}
          {precios === "margenes" && (
            <>
              <div><Label className="text-xs">% Margen mínimo</Label><Input type="number" value={pctMin} onChange={(e) => { setPctMin(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="editar-desglose-pct-min" /></div>
              <div><Label className="text-xs">% Margen sugerido</Label><Input type="number" value={pctSug} onChange={(e) => { setPctSug(e.target.value); setPreview(null); }} className="h-10 mt-1" data-testid="editar-desglose-pct-sug" /></div>
            </>
          )}
        </div>
        {!preview && (
          <Button onClick={calcular} disabled={loading} variant="outline" className="mt-3 h-11" data-testid="editar-desglose-calcular-btn">{loading ? "Calculando..." : "Ver qué cambiaría"}</Button>
        )}
        {preview && (
          <div className="mt-3 space-y-3" data-testid="editar-desglose-preview">
            {(preview.advertencias || []).length > 0 && (
              <div className="text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2" data-testid="editar-desglose-advertencias">
                {preview.advertencias.map((a, i) => <div key={i}>⚠ {a}</div>)}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              <Dato label="Factor" a={`×${preview.factor_anterior}`} b={`×${preview.factor_nuevo}`} />
              <Dato label="Stock" a={formatNumber(preview.stock_viejo)} b={formatNumber(preview.stock_nuevo)} testid="editar-desglose-stock" />
              <Dato label="Costo ficha" a={formatCOP(preview.costo_ficha_viejo || 0)} b={formatCOP(preview.costo_ficha_nuevo || 0)} testid="editar-desglose-costo-ficha" />
              <Dato label="Precio mín" a={formatCOP(preview.precio_minimo_viejo || 0)} b={formatCOP(preview.precio_minimo_nuevo || 0)} />
              <Dato label="Precio sug" a={formatCOP(preview.precio_sugerido_viejo || 0)} b={formatCOP(preview.precio_sugerido_nuevo || 0)} testid="editar-desglose-precio-sug-tile" />
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-1.5">Lote</th><th className="text-right p-1.5">Costo paquete</th><th className="text-right p-1.5">Disponible</th><th className="text-right p-1.5">Costo/unidad</th></tr></thead>
                <tbody>
                  {preview.filas.map((fl) => (
                    <tr key={fl.lote_id} className="border-t border-border/60 tabular-nums">
                      <td className="p-1.5">{fl.contenedor_id || "—"}{fl.vendido_despues > 0 && <span className="text-muted-foreground"> · {fl.vendido_despues} vendidas después</span>}</td>
                      <td className="p-1.5 text-right text-muted-foreground">{formatCOP(fl.costo_paquete)}</td>
                      <td className="p-1.5 text-right"><span className="text-muted-foreground line-through mr-1">{formatNumber(fl.cantidad_disponible)}</span>{formatNumber(fl.cantidad_disponible_nueva)}</td>
                      <td className="p-1.5 text-right"><span className="text-muted-foreground line-through mr-1">{formatCOP(fl.costo_viejo)}</span><b>{formatCOP(fl.costo_nuevo)}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: la bolsa trae 100 y no 20" className="h-11 mt-1" data-testid="editar-desglose-motivo" />
            </div>
            <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Se corrigen lotes, despachos y el costo congelado de los pedidos de este producto; Finanzas se actualiza al instante. Queda en auditoría.
            </div>
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="editar-desglose-cancelar-btn">Cancelar</Button>
          {preview && <Button onClick={aplicar} disabled={saving} className="bg-primary" data-testid="editar-desglose-aplicar-btn">{saving ? "Aplicando..." : "Aplicar corrección"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dato({ label, a, b, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-center gap-1 tabular-nums"><span className="text-muted-foreground line-through">{a}</span><ArrowRight className="w-3 h-3 text-primary" /><b>{b}</b></div>
    </div>
  );
}
